import { Injectable } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, finalize, firstValueFrom, map, Observable, shareReplay, Subject} from 'rxjs';
import { environment } from 'src/environments/environment';
import { CookieService } from 'ngx-cookie-service';

import { SignUp, User } from '../interfaces/user.type';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';

const USER_AUTH_API_URL =  environment.apiUrl + '/authentication';
const USUARIO_API_URL =  environment.apiUrl + '/usuario';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    private userContext$ = new BehaviorSubject<User>(null);
    private fetchUserContextInFlight$: Observable<User> | null = null;

    constructor(
      private http: HttpClient,
      private cookieService: CookieService,
      private router: Router,
      private messageService: NzMessageService
    ) {}

    public get userContext(): Observable<User> {
      return this.ensureUserContextLoaded();
    }

    public set userContext(user: any) {
      this.userContext$.next(user as User);
    }

    login(
      login: {
        email: string,
        senha: string,
        lembrar: boolean
      }
    ): Observable<{ msg: string; ativo: boolean; requiresActivation: boolean }> {
      return this.http.post<{ msg: string; ativo: boolean; requiresActivation: boolean }>(`${USER_AUTH_API_URL}/login`, login);
    }

    fetchUserContext(): Observable<User> {
      if (this.fetchUserContextInFlight$) {
        return this.fetchUserContextInFlight$;
      }

      this.fetchUserContextInFlight$ = this.http
        .get<{ user: any }>(`${USUARIO_API_URL}/contexto`)
        .pipe(
          map(({ user }) => {
            const userContext: User = {
              id: Number(user['id']),
              username: String(user['email']).split('@')[0],
              name: user['nome'],
              email: user['email'],
              password: null,
              token: user['uuid_token'],
              type: user['tipoUsuarioId'],
              ativo: user['ativo'] ?? true,
            };
            this.userContext$.next(userContext);
            return userContext;
          }),
          finalize(() => {
            this.fetchUserContextInFlight$ = null;
          }),
          shareReplay(1)
        );

      return this.fetchUserContextInFlight$;
    }

    logout() {
      this.http.get<{msg: string}>(`${USER_AUTH_API_URL}/logout`).subscribe({
        next: ({ msg }) => {
          this.messageService.success(msg);
          this.userContext$.next(null);
          this.router.navigate(['/authentication/login-1']);
        }
      })
    }

    activateAccount(payload: { novaSenha: string; novoEmail?: string }) {
      return this.http.post<{ msg: string; ativo: boolean; requiresActivation: boolean }>(`${USUARIO_API_URL}/ativar`, payload);
    }

    fetchActivationProfile(): Observable<{
      id: number;
      nome: string;
      email: string;
      empresa: string;
      telefone: string;
      localidade: string;
      tipoUsuarioId: number;
      ativo: boolean;
      cpf_cnpj: string;
    }> {
      return this.http.get<{
        id: number;
        nome: string;
        email: string;
        empresa: string;
        telefone: string;
        localidade: string;
        tipoUsuarioId: number;
        ativo: boolean;
        cpf_cnpj: string;
      }>(`${USUARIO_API_URL}/perfil-ativacao`);
    }

    async signUp(signUp: SignUp){
      let ip = '';
      try {
        ip = await firstValueFrom(this.http.get('https://api.ipify.org', {responseType: 'text'}));
      } catch (e) {
        console.error(e);
      }
      return this.http.post(`${USUARIO_API_URL}`, {...signUp, ip});
    }

    private ensureUserContextLoaded(): Observable<User> {
      if (this.userContext$.value) {
        return this.userContext$.asObservable();
      }
      return this.fetchUserContext();
    }

    recoveryPassword(recoveryPassFormValue: any) {
      return this.http.patch(`${USUARIO_API_URL}/recuperar-senha`, recoveryPassFormValue)
    }

    createNewPassword(updatePassPayload: any) {
      return this.http.post(`${USUARIO_API_URL}/criar-nova-senha`, updatePassPayload)
    }
}
